import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import ChatPanel from './ChatPanel';
import { apiHelpers, setAuthToken } from '@/api';
import { setAuthenticated, setSessionFlag } from '@/store/slices/authSlice';
import { useAuth } from '@/hooks/useAuth';
import toast from 'react-hot-toast';
import { extractErrorMessage } from '@/utils/errorHandling';
import { BP_LG, BP_MD } from '@/hooks/useBreakpoint';
import type { AppDispatch } from '@/store';

interface ChatWidgetProps {
  isAuthenticated: boolean;
  onAuthSuccess?: () => void;
}

type VerificationStep = 'email' | 'code' | 'register';

// Default sizes based on device — 초기 크기 1회 계산이라 훅이 아닌 함수. 임계 상수는 공용 값 사용
const getDefaultSize = () => {
  const isMobile = window.innerWidth < BP_MD;
  const isTablet = window.innerWidth < BP_LG;

  if (isMobile) {
    return { width: Math.min(window.innerWidth - 32, 380), height: Math.min(window.innerHeight - 180, 450) };
  } else if (isTablet) {
    return { width: 420, height: 550 };
  } else {
    return { width: 480, height: 650 };
  }
};

// Min/Max constraints
const MIN_WIDTH = 320;
const MAX_WIDTH = 800;
const MIN_HEIGHT = 400;
const MAX_HEIGHT = 900;

const ChatWidget: React.FC<ChatWidgetProps> = ({ isAuthenticated, onAuthSuccess }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { brandLogin: authBrandLogin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated);
  const modalRef = useRef<HTMLDivElement>(null);

  // Resize state
  const [size, setSize] = useState(() => {
    const saved = localStorage.getItem('chatWidgetSize');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return { width: parsed.width || 480, height: parsed.height || 600 };
      } catch {
        return getDefaultSize();
      }
    }
    return getDefaultSize();
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number; direction: string } | null>(null);

  // Email verification state
  const [verificationStep, setVerificationStep] = useState<VerificationStep>('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [registrationToken, setRegistrationToken] = useState('');
  const [registerForm, setRegisterForm] = useState({
    company_name: '',
    contact_person: '',
    phone: '',
  });

  // Password login state
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [isPasswordLoggingIn, setIsPasswordLoggingIn] = useState(false);

  // Sync isLoggedIn with isAuthenticated prop
  useEffect(() => {
    setIsLoggedIn(isAuthenticated);
  }, [isAuthenticated]);

  // Reset unread state when panel opens
  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
    }
  }, [isOpen]);

  // Reset verification state when closed
  useEffect(() => {
    if (!isOpen && !isLoggedIn) {
      setVerificationStep('email');
      setEmail('');
      setVerificationCode('');
      setIsNewUser(false);
      // Reset password login states
      setShowPasswordLogin(false);
      setLoginPassword('');
    }
  }, [isOpen, isLoggedIn]);

  // ESC key handler to close modal
  const handleEscKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [handleEscKey]);

  // Click outside handler to close modal
  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      // Check if click is on the toggle button (which has its own handler)
      const toggleButton = document.querySelector('[aria-label="Close chat"], [aria-label="Open chat"]');
      if (toggleButton && toggleButton.contains(e.target as Node)) {
        return;
      }
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, handleClickOutside]);

  // Resize handlers
  const handleResizeStart = useCallback((e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
      direction
    };
  }, [size]);

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (!isResizing || !resizeStartRef.current) return;

    const { x, y, width, height, direction } = resizeStartRef.current;
    const deltaX = e.clientX - x;
    const deltaY = e.clientY - y;

    let newWidth = width;
    let newHeight = height;

    // Handle different directions (resizing from top-left corner)
    if (direction.includes('w')) {
      newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, width - deltaX));
    }
    if (direction.includes('n')) {
      newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, height - deltaY));
    }

    setSize({ width: newWidth, height: newHeight });
  }, [isResizing]);

  const handleResizeEnd = useCallback(() => {
    if (isResizing) {
      setIsResizing(false);
      resizeStartRef.current = null;
      // Save to localStorage
      localStorage.setItem('chatWidgetSize', JSON.stringify(size));
    }
  }, [isResizing, size]);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleResizeMove);
      document.addEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = 'nwse-resize';
      document.body.style.userSelect = 'none';
      return () => {
        document.removeEventListener('mousemove', handleResizeMove);
        document.removeEventListener('mouseup', handleResizeEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Update size on window resize
  useEffect(() => {
    const handleWindowResize = () => {
      // Only adjust if current size exceeds new constraints
      setSize(prev => ({
        width: Math.min(prev.width, window.innerWidth - 32),
        height: Math.min(prev.height, window.innerHeight - 180)
      }));
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  const handleSendCode = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      await apiHelpers.auth.sendVerificationCode(email, 'chat');
      toast.success('Verification code sent to your email');
      setVerificationStep('code');
    } catch (error: any) {
      toast.error(extractErrorMessage(error.response?.data?.error) || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };

  // Password login handler
  const handlePasswordLogin = async () => {
    if (!email || !loginPassword) {
      toast.error('Please enter email and password');
      return;
    }

    setIsPasswordLoggingIn(true);
    try {
      await authBrandLogin(email, loginPassword);
      setIsLoggedIn(true);
      setShowPasswordLogin(false);
      setLoginPassword('');
      toast.success('Login successful!');
      onAuthSuccess?.();
    } catch (error: any) {
      toast.error(error.message || 'Invalid email or password');
    } finally {
      setIsPasswordLoggingIn(false);
    }
  };

  const handleVerifyCode = async (code?: string) => {
    const codeToVerify = code || verificationCode;
    if (!codeToVerify || codeToVerify.length < 6) {
      if (!code) toast.error('Please enter a valid verification code');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiHelpers.auth.verifyVerificationCode(email, codeToVerify);
      const data = (response.data?.data || response.data) as any;

      if (data?.newUser || data?.new_user) {
        // New user - need registration
        setIsNewUser(true);
        setRegistrationToken(data.registrationToken || data.registration_token || '');
        setVerificationStep('register');
      } else {
        // Existing user - logged in, update Redux state directly
        const accessToken = data.access_token;
        const user = data.user;
        const userType = (data.user_type || 'brand') as 'admin' | 'brand' | 'manufacturer';

        if (accessToken) {
          setAuthToken(accessToken);
          // Use setAuthenticated to properly set all auth state including userType
          dispatch(setAuthenticated({ accessToken, user, userType }));
          // Set session flag for page refresh persistence
          setSessionFlag();
          setIsLoggedIn(true);
          toast.success('Welcome back!');
          onAuthSuccess?.();
        } else {
          // Verification succeeded but no token - this shouldn't happen for existing users
          console.error('Verification succeeded but no access token for existing user');
          toast.error('Login failed. Please try again.');
          setVerificationStep('email');
          setVerificationCode('');
        }
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Invalid verification code';
      toast.error(errorMessage);
      // If code expired, allow resend
      if (errorMessage.includes('expired')) {
        setVerificationCode('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-verify when 6 digits are entered
  const handleCodeChange = (value: string) => {
    const newCode = value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(newCode);

    // Auto-submit when 6 digits are entered
    if (newCode.length === 6 && !isLoading) {
      handleVerifyCode(newCode);
    }
  };

  const handleRegister = async () => {
    if (!registerForm.company_name || !registerForm.contact_person) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiHelpers.auth.registerBrand({
        email,
        registration_token: registrationToken,
        ...registerForm,
      });
      const data = (response.data?.data || response.data) as any;
      const accessToken = data?.access_token;
      const user = data?.user;
      const userType = (data?.user_type || 'brand') as 'admin' | 'brand' | 'manufacturer';

      if (accessToken) {
        setAuthToken(accessToken);
        // Use setAuthenticated to properly set all auth state including userType
        dispatch(setAuthenticated({ accessToken, user, userType }));
        // Set session flag for page refresh persistence
        setSessionFlag();
        setIsLoggedIn(true);
        toast.success('Registration successful! Welcome!');
        onAuthSuccess?.();
      } else {
        // Registration succeeded but no token - stay on chat and show error
        console.error('Registration succeeded but no access token returned');
        toast.error('Registration completed but login failed. Please try logging in again.');
        // Reset to email step for retry
        setVerificationStep('email');
        setEmail('');
        setVerificationCode('');
        setIsNewUser(false);
        setRegistrationToken('');
        setRegisterForm({ company_name: '', contact_person: '', phone: '' });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Registration failed';
      toast.error(errorMessage);
      // If registration token expired, go back to email step
      if (errorMessage.includes('expired') || errorMessage.includes('Invalid')) {
        setVerificationStep('email');
        setVerificationCode('');
        setIsNewUser(false);
        setRegistrationToken('');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Unified chat modal with verification content
  const renderVerificationContent = () => (
    <div className="flex flex-col h-full bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden" style={{ overscrollBehavior: 'contain' }}>
      {/* Header - matches ChatPanel style */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-np-600 border-b border-np-700">
        <div className="flex items-center space-x-2">
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span className="text-white font-medium">NXP AI Assistant</span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-white/80 hover:text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Chat-like message area */}
      <div className="flex-1 overflow-y-auto p-4" style={{ overscrollBehavior: 'contain' }}>
        {/* AI Welcome Message */}
        <div className="flex items-start space-x-3 mb-4">
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-np-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-np-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div className="flex-1 bg-gray-100 rounded-lg p-3">
            <p className="text-sm text-gray-800">
              {verificationStep === 'email' && (
                <>Hello! I'm your AI assistant. Please enter your email to start our conversation.</>
              )}
              {verificationStep === 'code' && (
                <>Great! I've sent a verification code to <span className="font-medium text-np-600">{email}</span>. Please check your inbox and enter the code below.</>
              )}
              {verificationStep === 'register' && (
                <>Welcome! Since you're new here, please complete your registration to continue chatting.</>
              )}
            </p>
          </div>
        </div>

        {/* User response area */}
        {verificationStep === 'code' && (
          <div className="flex items-start justify-end space-x-3 mb-4">
            <div className="bg-np-600 rounded-lg p-3 max-w-[80%]">
              <p className="text-sm text-white">{email}</p>
            </div>
          </div>
        )}
      </div>

      {/* Input area - matches ChatPanel style */}
      <div className="flex-shrink-0 border-t border-gray-200 p-4 bg-gray-50">
        {verificationStep === 'email' && (
          <div className="space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email address..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-np-500 focus:border-transparent text-sm"
              onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
              autoFocus
            />
            <button
              onClick={handleSendCode}
              disabled={isLoading || !email}
              className="w-full py-3 bg-np-600 text-white rounded-lg hover:bg-np-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <span>Send Verification Code</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </>
              )}
            </button>

            {/* Password login option */}
            <div className="pt-2 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowPasswordLogin(!showPasswordLogin)}
                className="w-full text-xs text-np-600 hover:text-np-700 flex items-center justify-center gap-1"
              >
                <svg className={`w-3 h-3 transition-transform ${showPasswordLogin ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Already have an account? Login with password
              </button>
              {showPasswordLogin && (
                <div className="mt-3 p-3 bg-gray-100 rounded-lg">
                  <p className="text-xs text-gray-600 mb-2">
                    If you're a registered member, login with your password.
                  </p>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-np-500 focus:border-transparent mb-2"
                    placeholder="Enter your password"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handlePasswordLogin();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handlePasswordLogin}
                    disabled={isPasswordLoggingIn || !loginPassword || !email}
                    className="w-full py-2 bg-np-600 text-white rounded-lg hover:bg-np-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center justify-center"
                  >
                    {isPasswordLoggingIn ? (
                      <>
                        <svg className="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Logging in...</span>
                      </>
                    ) : (
                      'Login'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {verificationStep === 'code' && (
          <div className="space-y-3">
            <input
              type="text"
              value={verificationCode}
              onChange={(e) => handleCodeChange(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-np-500 focus:border-transparent text-sm text-center tracking-[0.5em] font-mono text-lg"
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyCode()}
              autoFocus
              disabled={isLoading}
            />
            <div className="flex space-x-2">
              <button
                onClick={() => setVerificationStep('email')}
                className="flex-1 py-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 text-sm font-medium transition-colors"
              >
                Back
              </button>
              <button
                onClick={() => handleVerifyCode()}
                disabled={isLoading || verificationCode.length < 6}
                className="flex-1 py-3 bg-np-600 text-white rounded-lg hover:bg-np-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center justify-center space-x-2"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <span>Verify</span>
                )}
              </button>
            </div>
            <button
              onClick={handleSendCode}
              disabled={isLoading}
              className="w-full py-2 text-np-600 hover:text-np-700 text-xs font-medium"
            >
              Didn't receive the code? Send again
            </button>
          </div>
        )}

        {verificationStep === 'register' && (
          <div className="space-y-3">
            <input
              type="text"
              value={registerForm.company_name}
              onChange={(e) => setRegisterForm(prev => ({ ...prev, company_name: e.target.value }))}
              placeholder="Company Name *"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-np-500 focus:border-transparent text-sm"
              autoFocus
            />
            <input
              type="text"
              value={registerForm.contact_person}
              onChange={(e) => setRegisterForm(prev => ({ ...prev, contact_person: e.target.value }))}
              placeholder="Your Name *"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-np-500 focus:border-transparent text-sm"
            />
            <input
              type="tel"
              value={registerForm.phone}
              onChange={(e) => setRegisterForm(prev => ({ ...prev, phone: e.target.value }))}
              placeholder="Phone (optional)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-np-500 focus:border-transparent text-sm"
            />
            <button
              onClick={handleRegister}
              disabled={isLoading || !registerForm.company_name || !registerForm.contact_person}
              className="w-full py-3 bg-np-600 text-white rounded-lg hover:bg-np-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Registering...</span>
                </>
              ) : (
                <>
                  <span>Complete Registration</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      {/* Chat Panel or Verification Content - Unified container */}
      {isOpen && (
        <div
          ref={modalRef}
          className="fixed z-50 bottom-32 right-4"
          style={{
            width: `${size.width}px`,
            height: `${size.height}px`,
            overscrollBehavior: 'contain',
          }}
        >
          {/* Resize handles - only visible on desktop */}
          <div className="hidden sm:block">
            {/* Top-left corner resize handle */}
            <div
              className="absolute -top-1 -left-1 w-4 h-4 cursor-nwse-resize z-10 group"
              onMouseDown={(e) => handleResizeStart(e, 'nw')}
            >
              <div className="absolute top-1 left-1 w-2 h-2 bg-gray-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            {/* Top edge resize handle */}
            <div
              className="absolute -top-1 left-4 right-4 h-2 cursor-ns-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 'n')}
            />
            {/* Left edge resize handle */}
            <div
              className="absolute top-4 -left-1 w-2 bottom-4 cursor-ew-resize z-10"
              onMouseDown={(e) => handleResizeStart(e, 'w')}
            />
          </div>

          {/* Size indicator during resize */}
          {isResizing && (
            <div className="absolute -top-8 left-0 bg-gray-800 text-white text-xs px-2 py-1 rounded">
              {size.width} x {size.height}
            </div>
          )}

          {isLoggedIn ? (
            <ChatPanel onClose={() => setIsOpen(false)} />
          ) : (
            renderVerificationContent()
          )}
        </div>
      )}

      {/* Toggle Button - Always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-16 right-4 z-50 w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
          isOpen
            ? 'bg-gray-700 hover:bg-gray-800'
            : 'bg-np-600 hover:bg-np-700'
        }`}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <>
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {hasUnread && (
              <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full" />
            )}
          </>
        )}
      </button>
    </>
  );
};

export default ChatWidget;
